import { useState } from 'react';
import { QRCodeDisplay } from '@/components/qr-code-display';

interface PhotoPreviewModalProps {
  isOpen: boolean;
  photoBlob: Blob | null;
  onRetake: () => void;
  onDone: () => void;
  uploadedUrl: string | null;
  isUploading: boolean;
}

export const PhotoPreviewModal = ({ 
  isOpen, 
  photoBlob, 
  onRetake,
  onDone,
  uploadedUrl,
  isUploading
}: PhotoPreviewModalProps) => {

  if (!isOpen || !photoBlob) {
    return null;
  }

  const photoUrl = URL.createObjectURL(photoBlob);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="min-w-2xl bg-gradient-to-b from-stone-100 to-stone-400 text-white p-8 rounded-2xl max-w-2xl w-full">
        
        {!isUploading && !uploadedUrl ? (
          <div className="space-y-4 text-center">
            {/* Photo Preview */}
            <div className="text-[2em] font-semibold mb-4 text-white">YOUR PHOTO</div>
            <div className="flex justify-center">
              <div className="bg-white p-4 rounded-lg border">
                <img 
                  src={photoUrl} 
                  alt="Captured photo" 
                  className="w-full h-full object-cover rounded"
                />
              </div>
            </div>
            <p className="text-white opacity-80">
              Processing your photo...
            </p>
          </div>
        ) : isUploading ? (
          <div className="space-y-4 text-center">
            {/* Loading state */}
            <div className="text-[2em] font-semibold mb-4 text-white">UPLOADING...</div>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            </div>
            <p className="text-white opacity-80">
              Please wait while we process your photo...
            </p>
          </div>
        ) : (
          <div className="space-y-4 text-center">
            {/* Photo preview and QR code side by side */}
            <div className="flex justify-center gap-4">
              <div className="rounded-sm p-4 bg-white border-black shadow-xl/30">
                <img 
                  src={photoUrl} 
                  alt="Captured photo" 
                  className="w-full h-full object-cover rounded-sm"
                />
              </div>
            </div>
            <div className="flex justify-center gap-4">
              <div className="bg-white p-2 rounded-lg">
                <QRCodeDisplay 
                  url={uploadedUrl!} 
                  size={128}
                />
                <span className="text-black text-xs m-0">SCAN TO DOWNLOAD</span>
              </div>
            </div>
            
            <div>
              <p className="text-lg mt-6 mb-6 text-white">
                Thanks for tagging us!<br/><span className="font-mono font-bold bg-sky-800 px-2 py-1 tracking-wider rounded">@AKAVIRTUAL_</span>
              </p>
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="flex gap-4 mt-8">
          <button
            onClick={onRetake}
            className="flex-1 bg-gray-700 border-gray-600 text-white hover:bg-gray-600 rounded-full py-3 px-6 font-medium transition-colors"
          >
            Take Another Photo
          </button>
          <button
            onClick={onDone}
            className="flex-1 bg-slate-200 font-bold text-black rounded-full hover:bg-yellow-400 py-3 px-6 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
